import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  useToast,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useMachineService } from '../../hooks/useMachineService';
import type { CordeCommonModelsDtoMachineMachine } from '@/api/generated/v1/api';

interface EditMachineModalProps {
  isOpen: boolean;
  onClose: () => void;
  machine: CordeCommonModelsDtoMachineMachine;
  onMachineUpdated: () => void;
}

export const EditMachineModal: React.FC<EditMachineModalProps> = ({
  isOpen,
  onClose,
  machine,
  onMachineUpdated,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    stream_url: '',
    stream_auth_token: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { updateMachine } = useMachineService();
  const toast = useToast();

  useEffect(() => {
    if (machine) {
      setFormData({
        name: machine.name || '',
        category: machine.category || '',
        stream_url: machine.stream_url || '',
        stream_auth_token: machine.stream_auth_token || '',
      });
    }
  }, [machine]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Machine name is required';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.stream_url.trim()) {
      newErrors.stream_url = 'Stream URL is required';
    } else {
      try {
        new URL(formData.stream_url);
      } catch {
        newErrors.stream_url = 'Please enter a valid URL';
      }
    }
    
    if (!formData.stream_auth_token.trim()) {
      newErrors.stream_auth_token = 'Auth token is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      await updateMachine(machine.id!, {
        name: formData.name,
        category: formData.category,
        stream_url: formData.stream_url,
        stream_auth_token: formData.stream_auth_token,
      });
      
      toast({
        title: 'Success',
        description: 'Machine updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onMachineUpdated();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update machine',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Machine</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isInvalid={!!errors.name}>
              <FormLabel>Machine Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter machine name"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.category}>
              <FormLabel>Category</FormLabel>
              <Select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                placeholder="Select category"
              >
                <option value="slots">Slots</option>
                <option value="poker">Poker</option>
                <option value="blackjack">Blackjack</option>
                <option value="roulette">Roulette</option>
                <option value="other">Other</option>
              </Select>
              <FormErrorMessage>{errors.category}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.stream_url}>
              <FormLabel>Stream URL</FormLabel>
              <Input
                value={formData.stream_url}
                onChange={(e) => handleInputChange('stream_url', e.target.value)}
                placeholder="Enter stream URL"
              />
              <FormErrorMessage>{errors.stream_url}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.stream_auth_token}>
              <FormLabel>Auth Token</FormLabel>
              <Input
                value={formData.stream_auth_token}
                onChange={(e) => handleInputChange('stream_auth_token', e.target.value)}
                placeholder="Enter auth token"
                type="password"
              />
              <FormErrorMessage>{errors.stream_auth_token}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Updating..."
          >
            Update Machine
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}; 